<template>
    <ion-page>
        <ion-header role="tablist" aria-label="order type toggle">
            <priceNotification :isDelivery="isDelivery" :showNotification="priceNotification" v-if="isMobile" />
        </ion-header>

        <gridContainer noPadding :forceOverscroll="false">
            <!-- Mobile View Location Detail -->
            <div v-if="isMobile">
                <ion-modal :isOpen="locationSlug" @didDismiss="redirectLogic()" canDismiss class="location-modal">
                    <storeLocationDetail v-if="locationSlug" :slug="locationSlug" v-on:close="close" @changeDelivery="isDelivery = true" showMap />
                </ion-modal>
            </div>

            <!-- Mobile View Search -->
            <ion-row class="ion-justify-content-between ion-padding mobile-row" id="pickupAndDeliveryContent" :class="{ 'mobile-deliver-row': isDelivery }" v-if="isMobile">
                <!-- Map & Toggle -->
                <ion-col size="12" class="map-col" style="margin-bottom: 30px">
                    <hereMap class="radius map" data-cy="mobile-map" :isDelivery="isDelivery" />
                    <orderTypeToggle v-if="isMobile" :showToolbar="!isDetailPage" v-model="isDelivery" data-cy="order-toggle" style="margin-top: -30px" />
                </ion-col>

                <!-- Pickup Tab -->
                <ion-col size="12" :class="{ 'ion-hide': isDelivery }">
                    <locationSearch v-if="!isDetailPage && isMobile && !isDelivery" class="map-search" data-cy="mobile-search" />
                    <storeLocationList v-if="!locationSlug" style="margin-bottom: 200px" />
                </ion-col>

                <!-- Delivery Tab -->
                <ion-col size="12" :class="{ 'ion-hide': !isDelivery || isDetailPage }">
                    <locationDelivery @changePickup="isDelivery = false" style="margin-bottom: 50px" />
                </ion-col>
            </ion-row>

            <!-- Desktop View -->
            <ion-card v-else class="mobile-hide-card location-card" :class="{ 'toggle-offset': !isDetailPage }">
                <ion-row class="ion-justify-content-between ion-align-items-start ion-padding pa-0 full-height" :class="{ 'price-offset': priceNotification, 'detail-page': isDetailPage }">
                    <!-- Price Notification -->
                    <ion-col size="12" class="pa-0" v-if="priceNotification">
                        <priceNotification :isDelivery="isDelivery" :showNotification="priceNotification" />
                    </ion-col>

                    <!-- Desktop Pickup Search -->
                    <ion-col size="12" size-md="5" class="radius pa-0 action-col" style="overscroll-behavior: none" :class="{ 'ion-hide': isDelivery }">
                        <orderTypeToggle :showToolbar="!isDetailPage" v-model="isDelivery" data-cy="order-toggle" />
                        <storeLocationList v-if="!locationSlug" searchBar :class="{ 'ion-hide': isDelivery }" class="desktop-list" />
                        <storeLocationDetail v-else :slug="locationSlug" v-on:close="close" @changeDelivery="isDelivery = true" />
                    </ion-col>

                    <!-- Desktop Delivery -->
                    <ion-col v-if="isDelivery && !isDetailPage" size="12" size-md="5" class="radius pa-0 action-col" style="overscroll-behavior: none">
                        <locationDelivery @changePickup="isDelivery = false" />
                    </ion-col>

                    <!-- Desktop Map -->
                    <ion-col size="12" size-md="7" class="map-col hide-overflow full-height">
                        <hereMap class="radius map" :isDelivery="isDelivery" />
                    </ion-col>
                </ion-row>
            </ion-card>
        </gridContainer>
    </ion-page>
</template>

<script lang="ts">
import { defineComponent, watch, ref, computed, onMounted, onUnmounted } from 'vue';

import { IonPage, IonModal, IonRow, IonCol, IonHeader, IonCard } from '@ionic/vue';
import { mapActions, mapGetters, useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';

import hereMap from '@/components/hereMap.vue';
import storeLocationDetail from '@/views/locations/storeLocationDetail.vue';
import storeLocationList from '@/views/locations/storeLocationList.vue';

import locationSearch from '@/components/locationSearch.vue';
import locationDelivery from '@/components/locationDelivery.vue';
import orderTypeToggle from '@/components/orderTypeToggle.vue';
import gridContainer from '@/components/layout/gridContainer.vue';
import priceNotification from '@/components/priceNotification.vue';
import { useUrlQueryParser } from '@/composables/useUrlQueryParser';

export default defineComponent({
    props: { slug: String },
    components: {
        IonPage,
        IonHeader,
        IonModal,
        gridContainer,
        IonRow,
        IonCol,
        IonCard,
        hereMap,
        locationSearch,
        orderTypeToggle,
        locationDelivery,
        storeLocationDetail,
        storeLocationList,
        priceNotification,
    },
    methods: {
        ...mapActions(['setNearLocationsByCoordinates']),
    },
    computed: {
        ...mapGetters(['userCoordinates', 'isFirstSearch', 'isGeoLocationOn']),
        isDetailPage() {
            return this.$router.currentRoute.value.name === 'Location Detail';
        },
    },

    created() {
        const store = useStore();
        store.dispatch('setGeoLocation');
        const locationsFound = store.getters.getLimitedNearLocations.length > 0;
        if ((this.isFirstSearch && this.isGeoLocationOn) || (this.isGeoLocationOn && !locationsFound)) {
            this.setNearLocationsByCoordinates(this.userCoordinates);
        }
    },
    setup() {
        const store = useStore();
        const isTablet = ref(false);
        //const isMobile = computed(() => store.getters.isMobile);
        const isMobile = ref(store.getters.isMobile);

        const isNative = computed(() => store.getters.isNative);
        const isDelivery = ref(store.getters.isDelivery);
        const route = useRoute();
        const router = useRouter();
        const locationSlug = ref();
        let redirectUrl = '/location';
        const forceClose = ref(false);
        const priceNotification = computed(() => route.query?.redirect == '/cart' || route.query?.redirect == '/checkout');
        const { getLocationSlugOrId, handleQueries } = useUrlQueryParser();
        handleQueries();

        const updateIsTablet = () => {
            isTablet.value = window.innerWidth > 768 && window.innerWidth <= 1024;
            isMobile.value = isTablet.value ? false : isMobile.value;
        };
        onMounted(() => {
            updateIsTablet();
            window.addEventListener('resize', updateIsTablet);
        });
        onUnmounted(() => {
            window.removeEventListener('resize', updateIsTablet);
        });

        const redirectLogic = (alternateRoute = undefined) => {
            if (!route.path.includes('location')) return;
            // @ts-ignore
            if (alternateRoute) redirectUrl = alternateRoute;
            if (route.query?.redirect && route.query?.redirect != redirectUrl) redirectUrl += `?redirect=${route.query?.redirect}`;
            router.push(redirectUrl);
            redirectUrl = '/location';
        };

        function close(alternateRoute) {
            forceClose.value = true;
            store.dispatch('mapHighlight', undefined);
            redirectLogic(alternateRoute);
        }

        // allow url param or query '?location=' or '?location_id='
        watch(route, (to) => (locationSlug.value = to.params.slug ?? getLocationSlugOrId()), { flush: 'pre', immediate: true, deep: true });

        return { locationSlug, close, isMobile, isNative, isDelivery, redirectLogic, forceClose, priceNotification, isTablet };
    },
});
</script>

<style scoped>
.action-col {
    overflow-y: auto;
}

.map-col {
    background: transparent;
    padding: 0;
}

.map {
    overflow: hidden;
}

.radius {
    border-radius: 6px;
}

.location-modal {
    --height: 100%;
    --width: 100%;
}

.mobile-deliver-row {
    max-height: calc(100vh - 54px);
    overflow-y: auto;
}

@media screen and (min-width: 960px) {
    .price-offset {
        padding-bottom: 41px !important;
    }

    .location-card {
        height: 86vh;
        margin-inline: 0;
    }

    .action-col {
        box-shadow: rgb(0 0 0 / 20%) 0px 3px 1px -2px, rgb(0 0 0 / 14%) 0px 2px 2px 0px, rgb(0 0 0 / 12%) 0px 1px 5px 0px;
    }

    .detail-page > .action-col,
    .detail-page > .map-col {
        height: calc(100%) !important;
    }

    .action-col,
    .map-col {
        height: calc(100%) !important;
    }

    ion-card {
        margin-top: 32px;
    }
}

@media screen and (max-width: 960px) {
    ion-col {
        max-height: unset;
    }

    .action-col {
        order: 2;
    }

    .radius {
        border-radius: 0;
    }

    .map-col {
        padding: 0;
        min-height: 33vh;
        max-height: 33vh;
        box-shadow: inset 0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12) !important;
    }

    ion-row {
        padding: 0;
    }

    .map-search {
        padding: 10px 30px;
        width: 100%;
    }
}
</style>
